import { Component, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChargeGroup, ChargeLineItem, ParsedBillData } from '../../../models';
import { SmartInputComponent } from '../../smart-input/smart-input.component';

export const MOVE_TARGETS: { value: keyof ParsedBillData; label: string }[] = [
    { value: 'supplyContractCharges', label: 'Supply Contract' },
    { value: 'supplyEnergyCharges', label: 'Supply Energy' },
    { value: 'supplyCapacityCharges', label: 'Supply Capacity' },
    { value: 'supplyTransmissionCharges', label: 'Supply Transmission' },
    { value: 'supplyMarketCharges', label: 'Supply Market' },
    { value: 'supplyLineLossCharges', label: 'Supply Line Loss' },
    { value: 'supplyGrtSut', label: 'Supply GRT & SUT' },
    { value: 'supplyMiscCharges', label: 'Supply Misc' },
    { value: 'supplySalesTaxes', label: 'Supply Sales Tax' },
    { value: 'distributionDemandCharges', label: 'Dist. Demand' },
    { value: 'distributionEnergyCharges', label: 'Dist. Energy' },
    { value: 'distributionCustomerCharges', label: 'Dist. Customer' },
    { value: 'distributionMiscCharges', label: 'Dist. Misc' },
    { value: 'distributionTaxCharges', label: 'Dist. Tax' },
];

/** Gas move-to targets — mirrors MOVE_TARGETS with the gas supply/distribution buckets. */
export const GAS_MOVE_TARGETS: { value: keyof ParsedBillData; label: string }[] = [
    { value: 'supplyCustomerCharges', label: 'Supply Customer' },
    { value: 'supplyGasCharges', label: 'Supply Gas' },
    { value: 'supplyOffPeakCharges', label: 'Supply Off Peak' },
    { value: 'supplyRateClassCharges', label: 'Supply Rate Class' },
    { value: 'supplyMiscCharges', label: 'Supply Misc' },
    { value: 'supplyGrtSut', label: 'Supply GRT & SUT' },
    { value: 'supplySalesTaxes', label: 'Supply Sales Tax' },
    { value: 'distributionCustomerCharges', label: 'Dist. Customer' },
    { value: 'distributionGasCharges', label: 'Dist. Gas' },
    { value: 'distributionDeliveryCharges', label: 'Dist. Delivery' },
    { value: 'distributionOffPeakCharges', label: 'Dist. Off Peak' },
    { value: 'distributionBalancingCharges', label: 'Dist. Balancing' },
    { value: 'distributionRateClassCharges', label: 'Dist. Rate Class' },
    { value: 'distributionMiscCharges', label: 'Dist. Misc' },
    { value: 'distributionGrtSut', label: 'Dist. GRT & SUT' },
    { value: 'distributionTaxCharges', label: 'Dist. Tax' },
];

/**
 * Where the "Move to..." menu may sit relative to its trigger button. Below and
 * right-aligned first (the original placement); the CDK falls through to the
 * later entries when the preferred one would leave the viewport, so a row at
 * the bottom of the dialog gets the menu above the button instead of clipped.
 */
const MOVE_MENU_POSITIONS: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
];

@Component({
  selector: 'app-charge-group-row',
  standalone: true,
  imports: [FormsModule, SmartInputComponent, OverlayModule],
  providers: [DecimalPipe],
  templateUrl: './charge-group-row.component.html',
  styleUrl: './charge-group-row.component.scss'
})
export class ChargeGroupRowComponent implements OnChanges {
    @Input() label: string = '';
    @Input() field!: keyof ParsedBillData;
    @Input() group: ChargeGroup = { total: 0, lineItems: [] };
    @Input() disabled: boolean = false;
    @Input() isGas: boolean = false;

    @Output() groupChange = new EventEmitter<ChargeGroup>();
    @Output() moveItem = new EventEmitter<{index: number, target: keyof ParsedBillData}>();

    expanded = false;
    moveMenuOpen: number | null = null;
    readonly moveMenuPositions = MOVE_MENU_POSITIONS;

    // Pre-computed for template (avoids method calls in @for loops)
    formattedAmounts: string[] = [];
    // Move-to targets split into Supply / Distribution columns so users don't
    // confuse the two and don't have to scroll one long mixed list.
    supplyMoveTargets: { value: keyof ParsedBillData; label: string }[] = [];
    distributionMoveTargets: { value: keyof ParsedBillData; label: string }[] = [];

    constructor(private decimalPipe: DecimalPipe) {}

    ngOnChanges(): void {
        this.formattedAmounts = (this.group?.lineItems || []).map(item => this.formatAmount(item.amount));
        const source = this.isGas ? GAS_MOVE_TARGETS : MOVE_TARGETS;
        const targets = source.filter(t => t.value !== this.field);
        this.supplyMoveTargets = targets.filter(t => String(t.value).startsWith('supply'));
        this.distributionMoveTargets = targets.filter(t => String(t.value).startsWith('distribution'));
    }

    get lineItemCount(): number {
        return this.group?.lineItems?.length || 0;
    }

    get hasData(): boolean {
        // Nonzero, not "> 0" — a negative total is a credit (DEV-15235), not an
        // empty group, so its label must not grey out.
        return this.lineItemCount > 0 || (this.group?.total || 0) !== 0;
    }

    calculateTotal(items: ChargeLineItem[]) {
        const total = items.reduce((sum, item) => sum + (item.amount || 0), 0);
        return this.roundMoney(total);
    }

    private roundMoney(value: number): number {
        return Math.round((value || 0) * 100) / 100;
    }

    formatAmount(amount: number): string {
        return this.decimalPipe.transform(amount || 0, '1.2-2') || '0.00';
    }

    parseAmount(value: string): number {
        // Strip commas and parse
        const cleanVal = value.replace(/,/g, '');
        const numVal = parseFloat(cleanVal);
        return isNaN(numVal) ? 0 : this.roundMoney(numVal);
    }

    // Track which line item is being edited (to prevent formatting during typing)
    editingAmountIndex: number | null = null;

    onAmountFocus(index: number) {
        this.editingAmountIndex = index;
    }

    onAmountInput(index: number, event: Event) {
        const input = event.target as HTMLInputElement;
        const amount = this.parseAmount(input.value);
        // Update the data model
        const newItems = [...this.group.lineItems];
        newItems[index] = { ...newItems[index], amount: this.roundMoney(amount) };
        const newTotal = this.calculateTotal(newItems);
        this.groupChange.emit({ total: newTotal, lineItems: newItems });
    }

    onAmountBlur(index: number, event: Event) {
        const input = event.target as HTMLInputElement;
        // Format the value on blur
        const amount = this.parseAmount(input.value);
        input.value = this.formatAmount(amount);
        this.editingAmountIndex = null;
    }

    updateLineItem(index: number, key: keyof ChargeLineItem, val: any) {
        const newItems = [...this.group.lineItems];
        // Round amount values to 2 decimal places
        const value = key === 'amount' ? this.roundMoney(Number(val)) : val;
        newItems[index] = { ...newItems[index], [key]: value };

        // Auto-update total
        const newTotal = this.calculateTotal(newItems);
        this.groupChange.emit({ total: newTotal, lineItems: newItems });
    }

    removeLineItem(index: number) {
        const newItems = this.group.lineItems.filter((_, i) => i !== index);
        const newTotal = this.calculateTotal(newItems);
        this.groupChange.emit({ total: newTotal, lineItems: newItems });
    }

    addLineItem() {
        const newItems = [...this.group.lineItems, { description: '', amount: 0 }];
        this.groupChange.emit({ ...this.group, lineItems: newItems });
        this.expanded = true;
    }

    handleTotalChange(val: string | number) {
        this.groupChange.emit({ ...this.group, total: this.roundMoney(Number(val)) });
    }

    toggleMoveMenu(index: number) {
        this.moveMenuOpen = this.moveMenuOpen === index ? null : index;
    }

    /**
     * Backdrop click / Escape / overlay detach for row `index`. Guarded so a
     * detach fired while another row's menu is opening can't close that one.
     */
    closeMoveMenu(index: number) {
        if (this.moveMenuOpen === index) {
            this.moveMenuOpen = null;
        }
    }

    handleMoveItem(index: number, target: keyof ParsedBillData) {
        this.moveItem.emit({ index, target });
        this.moveMenuOpen = null;
    }
}
